import type { Document } from '@contentful/rich-text-types';
import * as Sentry from '@sentry/react';
import type { ApolloError } from 'apollo-client';

import { useLocalizedContent } from 'bundles/internationalization-lib/hooks/useLocalizedContent';
import type { Locale } from 'bundles/shared-articles/types';
import type {
  SeoContentHubPageBottomContentLinksFragment,
  SeoContentHubPageFieldsFragment,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import {
  DEFAULT_LOCALE_CODE,
  LOCALIZED_HUB_PAGE_QUERY_FIELDS,
  PAGE_TYPE,
} from 'bundles/unified-seo-content-common/constants';
import { SeoContentHubPageFieldsFragment as SeoContentHubPageFieldsFragmentDoc } from 'bundles/unified-seo-content-common/queries/SeoContentHubPageQuery';
import type {
  NavigationLinkWithSubItems,
  SEOContentPageType,
  SeoContentCard,
  SeoContentFilter,
} from 'bundles/unified-seo-content-common/types';
import { getCategoryFilter, getNavigationLinks } from 'bundles/unified-seo-content-common/utils/hubPageUtils';

type InnerProps = {
  slug: string;
  pageType: SEOContentPageType;
  preview?: boolean;
  subfolderLocaleCode?: Locale;
};

export type PageDataProps = {
  title?: string | null;
  introduction?: string | null;
  featuredImageUrl?: string | null;
  featuredImageAlt?: string | null;
  featuredContent?: Array<SeoContentCard | null>;
  productRecommendationTitle?: string | null;
  productRecommendationsSearchUrl?: string | null;
  bottomContentLinks?: SeoContentHubPageBottomContentLinksFragment;
  bottomContentJson?: Document | null;
  discoveryCollectionSetId?: string | null;
  isHubPageDataLoading?: boolean;
  navigationLinks: NavigationLinkWithSubItems[];
  contentFilters: SeoContentFilter;
  metaTitle?: string | null;
  metaDescription?: string | null;
  error?: ApolloError;
};

const useSeoContentCategoryPageQuery = ({
  slug,
  pageType,
  preview = false,
  subfolderLocaleCode = DEFAULT_LOCALE_CODE,
}: InnerProps): PageDataProps => {
  const { languageCode, countryCode } = subfolderLocaleCode;
  /*
  Due to current limitation on localization system, we have to split out the query to fetch the list of content
  to be partially supported by the system. For more details, please see the design doc
  [here](https://docs.google.com/document/d/1SAQZuCdG68r5zQaIsBlukpBMiYf0mCwFHf_X9CF4xOA/edit#bookmark=id.k9u3o32hx0p)
  */
  const {
    data: hubPageData,
    loading: isHubPageDataLoading,
    error: hubPageDataError,
  } = useLocalizedContent<SeoContentHubPageFieldsFragment>({
    isPreview: preview,
    contentType: LOCALIZED_HUB_PAGE_QUERY_FIELDS.contentType,
    fragmentDoc: SeoContentHubPageFieldsFragmentDoc,
    fragmentFieldsName: LOCALIZED_HUB_PAGE_QUERY_FIELDS.fragmentFieldsName,
    filters: { slug, pageType, countryCode, hubPageType: PAGE_TYPE.categoryHub },
    countryCode,
    languageCode,
    onError: (errorInfo) =>
      Sentry.captureException(errorInfo, {
        extra: {
          message: 'Error fetching SEO content page data',
        },
      }),
  });

  const {
    title,
    introduction,
    featuredImage,
    filterBy,
    featuredContentCollection,
    bottomContent,
    productRecommendationTitle,
    productRecsSearchUrl,
    discoveryCollectionSetId,
    navigation,
    metaTitle,
    metaDescription,
  } = hubPageData || {};

  const { name: categoryName, __typename: categoryTypeName } = filterBy || {};
  const featuredContentSlugs = featuredContentCollection?.items?.map((item) => item?.slug ?? null);
  const filters: SeoContentFilter = {
    countryCode,
    languageCode,
    slug_not_in: featuredContentSlugs,
  };
  const categoryFilter = getCategoryFilter({ categoryName: categoryName ?? '', categoryTypeName });

  const contentFilters = { ...filters, ...categoryFilter };

  const { url: featuredImageUrl, description: featuredImageAlt } = featuredImage || {};
  const featuredContent = featuredContentCollection?.items;
  const { json: bottomContentJson, links: bottomContentLinks } = bottomContent || {};

  const navigationLinks = navigation ? getNavigationLinks(navigation, { countryCode, pageType }) : [];

  return {
    title,
    introduction,
    featuredImageUrl,
    featuredImageAlt,
    featuredContent,
    bottomContentJson,
    bottomContentLinks,
    discoveryCollectionSetId,
    isHubPageDataLoading,
    productRecommendationTitle,
    productRecommendationsSearchUrl: productRecsSearchUrl,
    navigationLinks,
    contentFilters,
    metaTitle,
    metaDescription,
    error: hubPageDataError,
  };
};

export default useSeoContentCategoryPageQuery;
